import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  alpha,
  styled,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LockIcon from '@mui/icons-material/Lock';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import {
  useRole,
  usePermissions,
  useCreateRole,
  useUpdateRole,
} from '../../hooks/useRoles';
import { PermissionMatrix } from '../../components/PermissionMatrix';
import type { Organization } from '../../types';

const fetchOrganizations = async (): Promise<Organization[]> => {
  const response = await axiosInstance.get('/organizations');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const StyledCard = styled(Card)(() => ({
  background: 'linear-gradient(to bottom right, #ffffff, #fafbfc)',
  borderRadius: '16px',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
}));

const HeaderBox = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
  padding: '48px 40px',
  borderRadius: '16px',
  marginBottom: '32px',
  boxShadow: '0 12px 40px rgba(124, 58, 237, 0.25)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)',
  },
}));

export const RoleEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { showMessage } = useSnackbar();

  const isEditMode = !!id;
  const initialOrgId = searchParams.get('organizationId');

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(initialOrgId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<{ name?: string }>({});

  // Fetch organizations for the dropdown
  const { data: organizations, isLoading: isLoadingOrgs } = useQuery({
    queryKey: ['organizations'],
    queryFn: fetchOrganizations,
  });

  // Fetch existing role if editing
  const { data: existingRole, isLoading: isLoadingRole } = useRole(id || null);

  // Fetch all permissions
  const { data: permissions, isLoading: isLoadingPermissions } = usePermissions();

  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole();

  // Populate form when editing
  useEffect(() => {
    if (existingRole) {
      setName(existingRole.name);
      setDescription(existingRole.description || '');
      setSelectedOrgId(existingRole.organizationId);
      const permIds = existingRole.rolePermissions?.map((rp) => rp.permissionId) || [];
      setSelectedPermissionIds(permIds);
    }
  }, [existingRole]);

  const validate = (): boolean => {
    const newErrors: { name?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Role name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    if (!selectedOrgId) {
      showMessage('Please select an organization', 'error');
      return;
    }

    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      permissionIds: selectedPermissionIds,
    };

    if (isEditMode && id) {
      updateMutation.mutate(
        { id, data: payload },
        {
          onSuccess: () => {
            showMessage('Role updated successfully', 'success');
            navigate('/roles');
          },
          onError: (error: any) => {
            showMessage(
              error.response?.data?.message || 'Failed to update role',
              'error'
            );
          },
        }
      );
    } else {
      createMutation.mutate(
        { organizationId: selectedOrgId, data: payload },
        {
          onSuccess: () => {
            showMessage('Role created successfully', 'success');
            navigate('/roles');
          },
          onError: (error: any) => {
            showMessage(
              error.response?.data?.message || 'Failed to create role',
              'error'
            );
          },
        }
      );
    }
  };

  const isLoading = isLoadingOrgs || isLoadingRole || isLoadingPermissions;
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isSystemDefault = existingRole?.isSystemDefault;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <HeaderBox>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <AdminPanelSettingsIcon sx={{ fontSize: 40, color: '#fff', opacity: 0.9 }} />
            <Typography
              variant="h3"
              sx={{
                color: '#fff',
                fontWeight: 700,
                fontSize: '36px',
                letterSpacing: '-0.02em',
              }}
            >
              {isEditMode ? 'Edit Role' : 'Create Role'}
            </Typography>
            {isSystemDefault && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  px: 2,
                  py: 0.5,
                }}
              >
                <LockIcon sx={{ fontSize: 16, color: '#fff' }} />
                <Typography
                  variant="body2"
                  sx={{ color: '#fff', fontWeight: 600 }}
                >
                  System Default
                </Typography>
              </Box>
            )}
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: '15px',
              maxWidth: '600px',
            }}
          >
            {isEditMode
              ? 'Modify role details and permissions'
              : 'Create a new custom role with specific permissions'}
          </Typography>
        </Box>
      </HeaderBox>

      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/roles')}
          sx={{
            textTransform: 'none',
            color: '#697386',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: alpha('#7c3aed', 0.05),
            },
          }}
        >
          Back to Roles
        </Button>
      </Box>

      {isSystemDefault && (
        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: '12px',
            '& .MuiAlert-icon': { color: '#6366f1' },
          }}
        >
          System default roles cannot be renamed or deleted. You can customize their
          permissions or clone them to create a custom variant.
        </Alert>
      )}

      <StyledCard sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h6"
            sx={{ mb: 3, fontWeight: 700, color: '#1a1f36' }}
          >
            Role Details
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Organization Selector */}
            <TextField
              select
              label="Organization"
              value={selectedOrgId || ''}
              onChange={(e) => setSelectedOrgId(e.target.value || null)}
              fullWidth
              disabled={isEditMode || isLoadingOrgs}
              helperText={
                isEditMode
                  ? 'Organization cannot be changed when editing'
                  : 'Select the organization this role belongs to'
              }
            >
              <MenuItem value="">
                <em>Select an organization...</em>
              </MenuItem>
              {organizations?.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Role Name */}
            <TextField
              label="Role Name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors({ ...errors, name: undefined });
              }}
              fullWidth
              required
              disabled={isSystemDefault}
              error={!!errors.name}
              helperText={
                errors.name ||
                (isSystemDefault
                  ? 'System default roles cannot be renamed'
                  : 'Enter a descriptive name for this role')
              }
              placeholder="e.g., Senior Staff, Inventory Manager"
            />

            {/* Description */}
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Describe the purpose and responsibilities of this role..."
              helperText="Optional description to help identify this role's purpose"
            />
          </Box>
        </CardContent>
      </StyledCard>

      <StyledCard sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h6"
            sx={{ mb: 1, fontWeight: 700, color: '#1a1f36' }}
          >
            Permissions
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 3, color: '#697386' }}
          >
            Select the permissions this role should have. Click row or column headers
            to toggle all permissions in that group.
          </Typography>

          <PermissionMatrix
            permissions={permissions || []}
            selectedPermissionIds={selectedPermissionIds}
            onChange={setSelectedPermissionIds}
            isLoading={isLoadingPermissions}
          />
        </CardContent>
      </StyledCard>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/roles')}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            padding: '10px 24px',
            fontWeight: 600,
            borderColor: '#e3e8ef',
            color: '#697386',
            '&:hover': {
              borderColor: '#c4c9d4',
              backgroundColor: '#fafbfc',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSubmit}
          disabled={isSaving || !selectedOrgId}
          sx={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
            borderRadius: '10px',
            padding: '10px 24px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            boxShadow: '0 4px 14px 0 rgba(124, 58, 237, 0.35)',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%)',
              boxShadow: '0 6px 20px 0 rgba(124, 58, 237, 0.45)',
              transform: 'translateY(-2px)',
            },
            '&:disabled': {
              background: '#e3e8ef',
              boxShadow: 'none',
            },
          }}
        >
          {isSaving ? 'Saving...' : isEditMode ? 'Update Role' : 'Create Role'}
        </Button>
      </Box>
    </Box>
  );
};

// Alias for create route
export const RoleCreate = RoleEdit;
